import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { ApiData } from '@upright/ui/api/common/hooks';
import { reactQueryStatusToRequestStatus } from '@upright/ui/api/common/types';
import { UseQueryResult } from '@tanstack/react-query';
import { keysOf } from '@upright/common/utils';
import { isArray, isBoolean, isNil, isNumber, isString, mapValues } from 'lodash';
import { isSsoRefreshErrorPayload } from '@upright/common/iam/login';
import { removeTokenFromStorageAndForceNewSsoLogin } from '../iam/sso';

type Option = boolean | string | readonly string[] | number | undefined;
export type Options = Record<string, Option>;

export function optionsToString(options?: Options): string {
  if (!options) {
    return '';
  }
  const optionsStrings = keysOf(options)
    .map(key => {
      const value = options[key];
      return serializeOption(key, value);
    })
    .filter(option => !isNil(option));
  return `?${optionsStrings.join('&')}`;
}

function serializeOption(key: string, option: Option) {
  const optionValue = serializeOptionValue(option);
  if (optionValue === undefined) {
    return undefined;
  }
  return `${encodeURIComponent(key)}=${encodeURIComponent(optionValue)}`;
}

function serializeOptionValue(option: Option): string | undefined {
  if (option === undefined) {
    return undefined;
  }
  if (isString(option)) {
    return option;
  }
  if (isArray(option)) {
    return `[${option.map(item => `"${item}"`).join(',')}]`;
  }
  if (isBoolean(option)) {
    return option ? 'true' : 'false';
  }
  if (isNumber(option)) {
    return `${option}`;
  }
  throw Error(`Unknown option type: ${typeof option}`);
}

// A function to convert a React Query into the ApiData format required by some old functions.
export function reactQueryToLegacyApiData<T>(query: UseQueryResult<T, ErrorWithHttpStatus>): ApiData<T> {
  return {
    status: query.error?.status,
    requestStatus: reactQueryStatusToRequestStatus({
      status: query.status,
      fetchStatus: query.fetchStatus,
    }),
    data: query.data,
    errorData: query.error
      ? // OA: The query.error.toString() will probably result in "[object, object]". Investigate and fix if there are any issues.
        // The error status and message should anyway be enough, so maybe this all is just some bad typing. I'm placating the linter here for now.
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        { statusCode: query.error.status, error: query.error.toString(), message: query.error.message }
      : undefined,
    ok: query.status === 'success',
    error: !!query.error,
  };
}

export function stringifyParamValues(params: Record<string, unknown>): Record<string, string> {
  return mapValues(params, value => JSON.stringify(value));
}

export async function maybeHandleCustomError(data: unknown | undefined) {
  if (isSsoRefreshErrorPayload(data)) {
    await removeTokenFromStorageAndForceNewSsoLogin(data.ssoProvider);
  }
}

// A helper for mocking React Query queries to make the UI already use React Query -like pattern for different kinds of data even though
// it all currently comes from a single big server call. This makes it effortless to untangle the single server call into multiple
// smaller ones in the future.
export function getMockQuery<Data>(
  query: {
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: ErrorWithHttpStatus | null;
    data: unknown;
  },
  mockIsLoading?: boolean,
) {
  const { isLoading, isError, isSuccess, error } = query;
  const mockQuery = { isLoading, isError, isSuccess, error, data: undefined as Data | undefined };
  if (mockIsLoading) mockQuery.isLoading = true;
  return mockQuery;
}

export function isUnauthorizedResponse<T extends { error?: ErrorWithHttpStatus | null }>(query: T) {
  if (!query.error) return false;
  return [401, 404].includes(query.error.status);
}
